// WaitingGroup — the messages sent while the assistant works (PS-61; pen
// "Studio · Action panel · Working", the "Waiting group" under the running
// step). Building blocks, each pen's own:
//   - Chat · Waiting message: the author bubble, outlined and dimmed, until
//     the assistant reads it at its next step.
//   - Chat · Message actions: on hover or keyboard focus, a popover INSIDE
//     the bubble (top right, over the text, nothing moves): Send now, Edit,
//     Delete.
//   - Chat · Deleted: "Deleted · Undo" in the place of a deleted message,
//     until the next message is sent or a step reads the queue. Consecutive
//     deletes (Delete all) are one row, one Undo.
//   - Chat · Waiting foot: always under the group: "Waiting · Send now · Edit
//     · Delete" for one, "{n} waiting · Send all now · Edit all · Delete
//     all" for several.
//
// Pure projection + command mapping: the rows come in (projected from the
// conversation's waiting messages), every click goes out as an intent. The
// popover's visibility is CSS (:hover / :focus-within), no state.

export type { WaitingActions };
export { splitQueued, WaitingGroup, waitingCeilingHint };

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../../Icon/Icon.js";
import type { AuthorEntry, ChatEntry } from "../types.js";
import "./WaitingGroup.css";

type Ids = readonly [string, ...string[]];

// What the author can do with waiting messages, one intent each; every one
// takes one message or several.
type WaitingActions = {
    readonly onSendNow: (messageIds: Ids) => void;
    readonly onEdit: (messageIds: Ids) => void;
    readonly onDelete: (messageIds: Ids) => void;
    readonly onUndo: (messageIds: Ids) => void;
};

// A row of the group: a waiting message, or a run of just-deleted ones.
type Row =
    | { readonly type: "waiting"; readonly entry: AuthorEntry }
    | { readonly type: "deleted"; readonly ids: Ids };

const rowsOf = (entries: readonly AuthorEntry[]): readonly Row[] =>
    entries.reduce<readonly Row[]>((rows, entry) => {
        if (entry.status?.type !== "deleted")
            return [...rows, { type: "waiting", entry }];
        const last = rows.at(-1);
        return last?.type === "deleted"
            ? [
                  ...rows.slice(0, -1),
                  { type: "deleted", ids: [...last.ids, entry.id] },
              ]
            : [...rows, { type: "deleted", ids: [entry.id] }];
    }, []);

const WaitingGroup = ({
    entries,
    actions,
}: {
    // In order; `status` waiting or deleted.
    readonly entries: readonly AuthorEntry[];
    readonly actions: WaitingActions;
}): ReactNode => {
    const { t } = useLingui();
    const waiting = entries.filter((e) => e.status?.type === "waiting");
    const [first, ...rest] = waiting.map((e) => e.id);
    // The foot's actions: for the one message, or for all of them.
    const one = rest.length === 0;
    const footActions: readonly (readonly [string, (ids: Ids) => void])[] = [
        [one ? t`Send now` : t`Send all now`, actions.onSendNow],
        [one ? t`Edit` : t`Edit all`, actions.onEdit],
        [one ? t`Delete` : t`Delete all`, actions.onDelete],
    ];
    return (
        <section
            className="bilda-waiting-group"
            aria-label={t`Messages waiting for the assistant`}
        >
            {rowsOf(entries).map((row) =>
                row.type === "deleted" ? (
                    <div key={row.ids.join(" ")} className="bilda-waiting-line">
                        <Icon
                            name="trash"
                            className="bilda-waiting-line__icon"
                        />
                        <span className="bilda-waiting-line__status">{t`Deleted`}</span>
                        <span aria-hidden="true">·</span>
                        <button
                            type="button"
                            className="bilda-waiting-line__action"
                            onClick={() => actions.onUndo(row.ids)}
                        >
                            {t`Undo`}
                        </button>
                    </div>
                ) : (
                    <WaitingMessage
                        key={row.entry.id}
                        entry={row.entry}
                        actions={actions}
                    />
                ),
            )}
            {first === undefined ? null : (
                <div className="bilda-waiting-line">
                    <Icon name="clock" className="bilda-waiting-line__icon" />
                    <span className="bilda-waiting-line__status">
                        {rest.length === 0
                            ? t`Waiting`
                            : t`${waiting.length} waiting`}
                    </span>
                    {footActions.map(([label, act]) => (
                        <span key={label} className="bilda-waiting-line__item">
                            <span aria-hidden="true">·</span>
                            <button
                                type="button"
                                className="bilda-waiting-line__action"
                                onClick={() => act([first, ...rest])}
                            >
                                {label}
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </section>
    );
};

// One waiting message, with its actions inside the bubble.
const WaitingMessage = ({
    entry,
    actions,
}: {
    readonly entry: AuthorEntry;
    readonly actions: WaitingActions;
}): ReactNode => {
    const { t } = useLingui();
    const ids: Ids = [entry.id];
    const buttons: readonly (readonly [
        IconName,
        string,
        (ids: Ids) => void,
    ])[] = [
        ["arrow-up", t`Send now`, actions.onSendNow],
        ["pencil", t`Edit`, actions.onEdit],
        ["trash", t`Delete`, actions.onDelete],
    ];
    return (
        <div className="bilda-waiting-message" data-entry-id={entry.id}>
            <p className="bilda-waiting-message__text">{entry.text}</p>
            <div className="bilda-waiting-message__actions">
                {buttons.map(([icon, label, act]) => (
                    <button
                        key={icon}
                        type="button"
                        className="bilda-waiting-message__action"
                        onClick={() => act(ids)}
                    >
                        <Icon
                            name={icon}
                            className="bilda-waiting-message__action-icon"
                        />
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
};

// The Limit hint at the CL-21 ceiling {max-waiting-messages}, shown above the
// composer's field; Send is off, the text stays (pen "Over the limit"). A
// descriptor: the owner localizes it.
const waitingCeilingHint = (max: number): MessageDescriptor =>
    msg`Up to ${max} messages can wait. Send more once these are read.`;

// The thread and the messages that wait (or were just deleted), which show
// grouped under the running step instead.
const splitQueued = (
    entries: readonly ChatEntry[],
): {
    readonly inThread: readonly ChatEntry[];
    readonly queued: readonly AuthorEntry[];
} => ({
    inThread: entries.filter((e) => !isQueued(e)),
    queued: entries.filter(isQueued),
});

const isQueued = (e: ChatEntry): e is AuthorEntry =>
    e.type === "author" &&
    (e.status?.type === "waiting" || e.status?.type === "deleted");
